import { isAxiosError } from 'axios';
import { getAxiosClient } from './common';

const axios = getAxiosClient();

// ---------------------------------------------------------------------------
// User layer config — client API.
//
// The per-user layer-config document is owned by `@civilgrid/db`
// (`schema/userLayerConfig.ts`). The client cannot import it — `@civilgrid/db`
// pulls drizzle/pg at module load — so these types are a deliberate client-side
// duplicate, validated server-side on every read/write; the client treats the
// document as opaque-but-typed transport. (Plain data with no such dependency is
// shared instead: see `@civilgrid/utils/src/layerConfig/defaultLayers`.)
// ---------------------------------------------------------------------------

/**
 * Per-**leaf** settings within a user's layer-config document. The document keys one
 * entry per `tile_url`/digitized leaf and nothing else — sections/groups/buckets are
 * never stored; structure, labels, and order are joined from the available-layer
 * catalog (`api/availableLayers.ts`) at render time. Presence of a leaf id =
 * the layer is in the user's picker/Library.
 */
export interface LayerNodeEntry {
  /** Is the picker checkbox on for this leaf (← PREFERRED_MAP_LAYERS.homeMap at seed time). */
  visible: boolean;
  /** Bookmark (← PINNED_LAYER_IDS). Omitted when not pinned. */
  pinned?: boolean;
  /**
   * Server-written stale-leaf GC stamp. The client serializes it back UNTOUCHED and
   * never interprets it — resetting or dropping it would break the server's retention
   * window for pruning genuinely removed layers.
   */
  missingSince?: string;
}

/**
 * The whole JSON document stored per org-member — leaf-only and state-minimal.
 * Expansion is NOT here — it is personal, device-local UI state, kept in localStorage
 * (`Prefs.EXPANDED_NODE_IDS`; no value → collapsed) and never serialized. Order is not
 * here either — siblings follow catalog position at render.
 */
export interface LayerConfigDocument {
  /** One entry per leaf the user's picker/Library contains, keyed by leaf id. */
  nodes: Record<string, LayerNodeEntry>;
}

/**
 * Body for `POST /v1/layerConfig/seed` — the legacy localStorage state read once at first
 * use: `lastUsedLayerIds` from the per-map `PREFERRED_MAP_LAYERS.homeMap`, pins from
 * the global `PINNED_LAYER_IDS`. Field names mirror the server's `SeedLayerConfigBodySchema`
 * exactly; ids the org cannot have are dropped server-side, not rejected.
 */
export interface SeedLayerConfigBody {
  lastUsedLayerIds: string[];
  pinnedLayerIds: string[];
}

/**
 * Outcome of a `GET /v1/layerConfig`. The `absent`-vs-`error` distinction is load-bearing
 * for the no-overwrite invariant: `absent` is the definitive "safe to seed" signal, while
 * `error` (denial / 5xx / network / uncertain) must NEVER be treated as absent — a
 * misclassified absence would trigger a seed that clobbers a real config. The store arms
 * persistence only on `found`/seed success, never on `error`.
 *
 * `absent` comes ONLY from the server's in-body `{ status: 'not_seeded' }` — a 200 from a
 * request that was authorized, well-formed, and positively confirmed no row exists. It is
 * deliberately NOT inferred from a transport 404: the membership middleware (malformed
 * request) and routing/proxies also produce 404s, and reading those as "safe to seed"
 * would fire a spurious seed. Every 404 maps to `error`.
 */
export type LayerConfigGetResult =
  | { status: 'found'; document: LayerConfigDocument }
  | { status: 'absent' }
  | { status: 'error'; httpStatus?: number };

/** Wire shape of a 200 from `GET /v1/layerConfig` (server: `controllers/userLayerConfig.ts`). */
type LayerConfigGetResponse = { status: 'found'; document: LayerConfigDocument } | { status: 'not_seeded' };

const LAYER_CONFIG_PATH = '/v1/layerConfig';

/**
 * `GET /v1/layerConfig?organizationId=`. Returns a discriminated result rather than
 * throwing, so the caller can branch on `found` / `absent` / `error` without conflating
 * absence with failure. An unexpected 2xx body shape is `error`, not `absent` — ambiguity
 * must never read as "safe to seed".
 */
export async function getLayerConfig(organizationId: string): Promise<LayerConfigGetResult> {
  try {
    const { data } = await axios.get<LayerConfigGetResponse>(LAYER_CONFIG_PATH, {
      params: { organizationId },
    });
    if (data?.status === 'found' && data.document) return { status: 'found', document: data.document };
    if (data?.status === 'not_seeded') return { status: 'absent' };
    return { status: 'error' };
  } catch (e) {
    return { status: 'error', httpStatus: isAxiosError(e) ? e.response?.status : undefined };
  }
}

/**
 * `POST /v1/layerConfig/seed?organizationId=`. First-use seed: the server merges the
 * posted localStorage arrays with the app-wide defaults and persists, returning the seeded
 * document (idempotent — an existing row is returned unchanged). Rejects on failure; the
 * caller treats a rejection as a load failure (no seed retry, sync left disarmed).
 */
export async function seedLayerConfig(organizationId: string, body: SeedLayerConfigBody): Promise<LayerConfigDocument> {
  const { data } = await axios.post<LayerConfigDocument>(`${LAYER_CONFIG_PATH}/seed`, body, {
    params: { organizationId },
  });
  return data;
}

/**
 * `PUT /v1/layerConfig?organizationId=`. Replace the whole document — last-write-wins,
 * validated server-side. Backs the debounced sync and its
 * lifecycle flushes (route change / tab hidden / unload); the caller
 * (`store2/layerConfigSync.ts`) gates every call on `canPersist`, so a `PUT` only ever
 * carries state hydrated from a successful `200` (no-overwrite invariant). Writes
 * are serialized (one in flight at a time), so they cannot reach the server out of order.
 * Rejects on failure; the caller retains dirty and retries on the next flush.
 */
export async function putLayerConfig(organizationId: string, document: LayerConfigDocument): Promise<void> {
  await axios.put(LAYER_CONFIG_PATH, document, { params: { organizationId } });
}
